import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { of, Observable, from } from 'rxjs';
import {
    map,
    switchMap,
    withLatestFrom,
    catchError,
    filter,
    take, tap
} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { ManagementApiService } from '../management-api.service';
import { ManagementStoreService } from '../management-store.service';
import { ManagementBaseActions } from '@emm/management/store/base/management-base.actions';
import {RouterActions} from '@emm/core/store/router.actions';
import {ItemsActions} from '@emm/management/store/items/items.actions';
import {AttributesActions} from '@emm/management/store/attributes/attributes.actions';
import {ItemTypesActions} from '@emm/management/store/item-types/item-types.actions';
import {Item} from '@emm/core/models/item.model';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {ItemGroupingsActions} from '@emm/management/store/item-groupings/item-groupings.actions';
import {ItemTypeClassesActions} from '@emm/management/store/item-type-classes/item-type-classes.actions';
import {ItemType} from '@emm/core/models/item-type.model';
import {LocationSetTypesActions} from '@emm/management/store/location-set-types/location-set-types.actions';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import { Attribute } from '@emm/core/models/attribute.model';
import {OperatorLocksActions} from '@emm/management/store/emergency-items/operator-locks.actions';
import {ConsumerTypesActions} from '@emm/management/store/consumer-types/consumer-types.actions';
import {MasterMenuSystemActions} from '@emm/management/store/mms-revote/master-menu-system.actions';
import {LocationSetsActions} from '@emm/management/store/location-sets/location-sets.actions';
import {RouterStoreService} from '@emm/core/store/router-store.service';
import {ManagementItemsStoreService} from '@emm/management/store/items/management-items-store.service';


@Injectable({ providedIn: 'root' })
export class ManagementBaseEffects {

    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.init),
            switchMap( () => this.api.getScreens()
                .pipe(
                    map((screens) => ManagementBaseActions.initSuccess({ screens }))
                )
            )
        )
    );

    changeScreen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.changeScreen),
            filter(({ navigate }) => {
                return RA.isTrue(navigate);
            }),
            switchMap(({ screen }) =>
                of(
                    RouterActions.go({
                        path: ['management', screen.route],
                    })
                )
            )
        )
    );

    navigateSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(routerNavigatedAction),
                switchMap((_) => this.store.queryBase.initialized$),
                filter(RA.isTrue),
                withLatestFrom(this.routerStore.selectUrl()),
                map(R.last),
                filter(R.includes('/management/')),
                withLatestFrom(this.routerStore.selectRouteData()),
                map(R.last),
                map(name => name),
                switchMap((name: string) =>
                    of(ManagementBaseActions.changeScreen({ screen: { name }, navigate: false }))
                ),
                take(1)
            ) as Observable<Action>
    );

    initItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initItems),
            withLatestFrom(this.itemStore.queries.initSize$),
            switchMap(([_, initSize]) => {
                return from([
                    ItemsActions.loadBySize({ initSize }),
                    AttributesActions.loadGlobalAttributes(),
                    ItemTypesActions.loadAllForItems(),
                ]);
            })
        )
    );

    selectItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemsActions.select),
            switchMap(() => from([ManagementBaseActions.loadItemDetail()]))
        )
    );



    loadItemDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadItemDetail),
            withLatestFrom(this.store.queryItems.selected$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedItem: Item) =>
                this.api.getItem(selectedItem.tag).pipe(
                    map((item) => ManagementBaseActions.loadItemDetailSuccess({ item })),
                    catchError(({ error }) => of(ManagementBaseActions.loadItemDetailFailure({ error })))
                )
            )
        )
    );



    loadItemLocaleDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadItemLocaleDetail),
            switchMap(({ tag, country, language }) => (
                this.api.getItemLocale(tag, country, language).pipe(
                    map((itemLocale) => ManagementBaseActions.loadItemLocaleDetailSuccess({ itemLocale })),
                    catchError(({ error }) => of(ManagementBaseActions.loadItemLocaleDetailFailure({ error })))
                )
            )
        )
    ));



    updateItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateItem),
            switchMap(({ tag, updates, dispatchReload }) =>
                this.api.updateItem(tag, updates).pipe(
                    map((item) => {
                        const itemWithUpdatedName = { ...item };
                        itemWithUpdatedName.name = (item.attributes as ItemAttribute[]).find(
                            (attribute) => attribute.tag === 'NAME' && attribute.countryCode === 'US'
                        )?.value;

                        return ManagementBaseActions.updateItemSuccess({ item: itemWithUpdatedName, dispatchReload });
                    }),
                    catchError(({ error }) => of(ManagementBaseActions.updateItemFailure({ error })))
                )
            )
        )
    );

    initItemGroupings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initItemGroupings),
            switchMap(() =>
                from([
                    ItemGroupingsActions.loadItemGroups(),
                    ItemTypesActions.loadAllForItemGroups(),
                    AttributesActions.loadGlobalAttributes(),
                ])
            )
        )
    );

    selectItemGrouping$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.select),
            switchMap(({ tag }) => of(ManagementBaseActions.loadItemGroupingDetail({ tag })))
        )
    );

    loadItemGroupingDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadItemGroupingDetail),
            switchMap(({ tag }) =>
                this.api.getItem(tag).pipe(
                    map((item) => ManagementBaseActions.loadItemGroupingDetailSuccess({ item })),
                    catchError(({ error }) => of(ManagementBaseActions.loadItemGroupingDetailFailure({ error })))
                )
            )
        )
    );

    initItemTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initItemTypes),
            switchMap(() => from([ItemTypesActions.loadAll(), ItemTypeClassesActions.load()]))
        )
    );

    selectItemType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.select),
            switchMap(() => from([ManagementBaseActions.loadItemTypeDetail()]))
        )
    );

    loadItemTypeDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadItemTypeDetail),
            withLatestFrom(this.store.queryItemTypes.selected$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedItemType: ItemType) =>
                this.api.getItemType(selectedItemType.tag).pipe(
                    map((itemType) => ManagementBaseActions.loadItemTypeDetailSuccess({ itemType })),
                    catchError(({ error }) => of(ManagementBaseActions.loadItemTypeDetailFailure({ error })))
                )
            )
        )
    );

    updateItemType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateItemType),
            switchMap(({ tag, updates, dispatchReload }) =>
                this.api.updateItemType(tag, updates).pipe(
                    map((itemType) => ManagementBaseActions.updateItemTypeSuccess({ itemType, dispatchReload })),
                    catchError(({ error }) => of(ManagementBaseActions.updateItemTypeFailure({ error })))
                )
            )
        )
    );

    initLocationSetTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initLocationSetTypes),
            switchMap(() => from([LocationSetTypesActions.load()]))
        )
    );

    selectLocationSetType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetTypesActions.select),
            switchMap(() => from([ManagementBaseActions.loadLocationSetTypeDetail()]))
        )
    );

    loadLocationSetTypeDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadLocationSetTypeDetail),
            withLatestFrom(this.store.queryLocationSetTypes.selected$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedLocationSetType: LocationSetType) =>
                this.api.getLocationSetType(selectedLocationSetType.tag).pipe(
                    map((locationSetType) =>
                        ManagementBaseActions.loadLocationSetTypeDetailSuccess({ locationSetType })
                    ),
                    catchError(({ error }) => of(ManagementBaseActions.loadLocationSetTypeDetailFailure({ error })))
                )
            )
        )
    );

    updateLocationSetType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateLocationSetType),
            switchMap(({ tag, updates, dispatchReload }) =>
                this.api.updateLocationSetType(tag, updates).pipe(
                    map((locationSetType) =>
                        ManagementBaseActions.updateLocationSetTypeSuccess({ locationSetType, dispatchReload })
                    ),
                    catchError(({ error }) => of(ManagementBaseActions.updateLocationSetTypeFailure({ error })))
                )
            )
        )
    );

    initLocationSets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initLocationSets),
            switchMap(() => from([LocationSetTypesActions.loadFromLocationSets()]))
        )
    );

    updateLocationSet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateLocationSet),
            switchMap(({ tag, updates, dispatchReload }) =>
                this.api.updateLocationSet(tag, updates).pipe(
                    map((locationSet) => {
                        const locationSetWithUpdatedName = { ...locationSet };

                        return ManagementBaseActions.updateLocationSetSuccess({
                            locationSet: locationSetWithUpdatedName,
                            dispatchReload,
                        });
                    }),
                    catchError(({ error }) => of(ManagementBaseActions.updateLocationSetFailure({ error })))
                )
            )
        )
    );

    initAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initAttributes),
            switchMap(() => from([AttributesActions.load()]))
        )
    );

    selectAttribute$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttributesActions.select),
            switchMap(() => from([ManagementBaseActions.loadAttributeDetail()]))
        )
    );

    loadAttributeDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadAttributeDetail),
            withLatestFrom(this.store.queryAttributes.selected$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedAttribute: Attribute) =>
                this.api.getAttribute(selectedAttribute.tag).pipe(
                    map((attribute) => ManagementBaseActions.loadAttributeDetailSuccess({ attribute })),
                    catchError(({ error }) => of(ManagementBaseActions.loadAttributeDetailFailure({ error })))
                )
            )
        )
    );

    initOperatorEmergency$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initOperatorEmergency),
            switchMap(() => from([OperatorLocksActions.loadAllLocationStatuses()]))
        )
    );

    initConsumerTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initConsumerTypes),
            switchMap(() => from([ConsumerTypesActions.loadAll()]))
        )
    );

    initMmsAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initMmsAdmin),
            switchMap(() => {
                return from([
                    ManagementBaseActions.loadAllLocation()
                ]);
            })
        )
    );

    initMasterMenuSystem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initMasterMenuSystem),
            switchMap(() => {
                return from([
                    MasterMenuSystemActions.loadAll(),
                    MasterMenuSystemActions.loadAllLocations({ consumer: 'MENU_ALIGNMENT' }),
                    MasterMenuSystemActions.loadAllCategories({ consumer: 'MENU_ALIGNMENT' }),
                    LocationSetTypesActions.load(),
                    LocationSetsActions.loadAllByScope()
                ]);
            })
        )
    );

    selectRevote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.select),
            switchMap(() => from([ManagementBaseActions.loadRevoteDetail()]))
        )
    );

    loadRevoteDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadRevoteDetail),
            withLatestFrom(this.store.queryMasterMenu.selectedRevote$),
            map(R.last),
            filter(RA.isNotNil),
            switchMap((selectedRevote: any) =>
                this.api.getRevote(selectedRevote.id).pipe(
                    map((revote) => ManagementBaseActions.loadRevoteDetailSuccess({ revote })),
                    catchError(({ error }) => of(ManagementBaseActions.loadRevoteDetailFailure({ error })))
                )
            )
        )
    );

    updateRevote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateRevote),
            switchMap(({ id, updates, dispatchReload }) =>
                this.api.updateRevote(id, updates).pipe(
                    map((revote) => ManagementBaseActions.updateRevoteSuccess({ revote, dispatchReload })),
                    catchError(({ error }) => of(ManagementBaseActions.updateRevoteFailure({ error })))
                )
            )
        )
    );

    updateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.updateRevoteSuccess),
            switchMap(() => of(MasterMenuSystemActions.loadAll()))
        )
    );

    initLanguageAssignments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.initLanguageAssignments),
            switchMap(() => {
                return from([
                    ManagementBaseActions.loadAllLocation()
                ]);
            })
        )
    );

    loadAllLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ManagementBaseActions.loadAllLocation),
            switchMap(() =>
                this.api.getAllLocationSetsByTag('LOCATION').pipe(
                    map((locationSets) => ManagementBaseActions.loadAllLocationSuccess({ locationSets })),
                    catchError(({ error }) => of(ManagementBaseActions.loadAllLocationFailure({ error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        private store: ManagementStoreService,
        private itemStore: ManagementItemsStoreService,
        private routerStore: RouterStoreService,
    ) {}

}
